import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { useLocale, useT } from "../i18n";
import { Seo } from "../components/Seo";
import { CountryField, Area, FormFooter, FormGroup, FormLayout, NextSteps, PageHero, Success, Text, check, toInt, useRequestForm } from "../components/forms/FormKit";

const P = "sell";
const MAX = 10;
const TYPES = /^image\/(jpeg|png|webp|heic|heif)$/;
const okFile = (f) => (TYPES.test(f.type) || /\.(heic|heif)$/i.test(f.name)) && f.size <= 25 * 1024 * 1024;

const Photos = ({ files, setFiles }) => {
  const t = useT();
  const input = useRef(null);
  const [note, setNote] = useState("");
  const previews = useRef(new Map());
  useEffect(() => () => previews.current.forEach((u) => URL.revokeObjectURL(u)), []);
  const url = (f) => {
    if (!previews.current.has(f)) previews.current.set(f, URL.createObjectURL(f));
    return previews.current.get(f);
  };
  const add = (list) => {
    const incoming = [...list];
    const valid = incoming.filter(okFile);
    const room = MAX - files.length;
    setNote(valid.length < incoming.length ? t("sellPage.photosType") : valid.length > room ? t("sellPage.photosTooMany") : "");
    setFiles([...files, ...valid.slice(0, Math.max(0, room))]);
  };
  const remove = (f) => {
    URL.revokeObjectURL(previews.current.get(f));
    previews.current.delete(f);
    setFiles(files.filter((x) => x !== f));
  };
  return (
    <div data-testid="sell-photos">
      <div className="flex flex-wrap items-center gap-4">
        <button type="button" onClick={() => input.current?.click()} disabled={files.length >= MAX} className="btn btn-ghost-dark disabled:opacity-40" data-testid="sell-photos-add">{t("sellPage.addPhotos")}</button>
        <span className="text-xs text-ink/50">{files.length}/{MAX} · {t("sellPage.photosHint")}</span>
      </div>
      <input ref={input} type="file" multiple accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" className="hidden"
        onChange={(e) => { add(e.target.files); e.target.value = ""; }} data-testid="sell-photos-input" />
      {note && <p className="mt-2 text-[13px] text-[#9f1d24]" data-testid="sell-photos-note">{note}</p>}
      {files.length > 0 && (
        <ul className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-5">
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} className="relative aspect-square bg-mist" data-testid={`sell-photo-${i}`}>
              <img src={url(f)} alt="" className="h-full w-full object-cover" />
              <button type="button" onClick={() => remove(f)} aria-label={t("sellPage.remove")} className="absolute right-0 top-0 flex h-11 w-11 items-center justify-center bg-ink/70 text-white" data-testid={`sell-photo-remove-${i}`}>
                <X size={16} strokeWidth={1.5} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default function SellPage() {
  const t = useT();
  const lang = useLocale();
  const [files, setFiles] = useState([]);
  const ctx = useRequestForm({ make: "", model: "", grade: "", model_year: "", mileage_km: "", chassis_or_code: "", condition_notes: "", asking_price_jpy: "",
    location: "", country: lang === "ja" ? "JP" : "", name: "", email: "", phone: "", message: "", consent: false, website: "" });
  const { form } = ctx;

  const submit = (e) => {
    e.preventDefault();
    const errs = check(form, { make: { req: true, max: 60 }, model: { req: true, max: 80 }, grade: { max: 80 }, model_year: { int: true }, mileage_km: { int: true },
      chassis_or_code: { max: 60 }, condition_notes: { max: 3000 }, asking_price_jpy: { int: true }, location: { req: true, max: 100 },
      name: { req: true, max: 100 }, email: { req: true, email: true, max: 200 }, phone: { max: 40 }, message: { max: 3000 } }, t);
    ctx.send("/requests/sell", errs, (fill_ms) => {
      const body = new FormData();
      body.append("data", JSON.stringify({
        locale: lang, make: form.make.trim(), model: form.model.trim(), grade: form.grade.trim(), model_year: toInt(form.model_year), mileage_km: toInt(form.mileage_km),
        chassis_or_code: form.chassis_or_code.trim(), condition_notes: form.condition_notes.trim(), asking_price_jpy: toInt(form.asking_price_jpy),
        location: form.location.trim(), country: form.country || null, name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(), message: form.message.trim(),
        consent: form.consent, website: form.website, fill_ms,
      }));
      files.forEach((f) => body.append("photos", f));
      return body;
    });
  };
  const again = () => {
    setFiles([]);
    ctx.reset();
  };

  return (
    <div data-testid="sell-page">
      <Seo title={t("pages.sell.title")} description={t("pages.sell.description")} path="sell" />
      <PageHero eyebrow={t("sellPage.eyebrow")} title={t("sellPage.title")} intro={t("sellPage.intro")} />
      <FormLayout aside={<NextSteps label={t("pages.sell.title")} />}>
        {ctx.phase === "success" ? <Success prefix={P} onAgain={again} /> : (
          <form onSubmit={submit} noValidate className="grid gap-10" data-testid="sell-form">
            <FormGroup title={t("sellPage.vehicle")}>
              <div className="grid gap-5 sm:grid-cols-2">
                <Text ctx={ctx} prefix={P} k="make" label={t("sellPage.make")} />
                <Text ctx={ctx} prefix={P} k="model" label={t("sellPage.model")} />
                <Text ctx={ctx} prefix={P} k="grade" label={t("sellPage.grade")} optional />
                <Text ctx={ctx} prefix={P} k="model_year" label={t("sellPage.year")} inputMode="numeric" optional />
                <Text ctx={ctx} prefix={P} k="mileage_km" label={t("sellPage.mileage")} inputMode="numeric" optional />
                <Text ctx={ctx} prefix={P} k="chassis_or_code" label={t("sellPage.chassis")} optional />
                <Text ctx={ctx} prefix={P} k="asking_price_jpy" label={t("sellPage.askingPrice")} inputMode="numeric" optional />
                <CountryField ctx={ctx} prefix={P} k="country" label={t("form.country")} optional />
                <Text ctx={ctx} prefix={P} k="location" label={t("sellPage.location")} />
              </div>
              <Area ctx={ctx} prefix={P} k="condition_notes" label={t("sellPage.conditionNotes")} placeholder={t("sellPage.conditionPlaceholder")} optional />
            </FormGroup>
            <FormGroup title={t("sellPage.photos")}>
              <Photos files={files} setFiles={setFiles} />
            </FormGroup>
            <FormGroup title={t("form.yourDetails")}>
              <div className="grid gap-5 sm:grid-cols-2">
                <Text ctx={ctx} prefix={P} k="name" label={t("form.name")} autoComplete="name" className="sm:col-span-2" />
                <Text ctx={ctx} prefix={P} k="email" label={t("form.email")} type="email" autoComplete="email" inputMode="email" />
                <Text ctx={ctx} prefix={P} k="phone" label={t("form.phone")} type="tel" autoComplete="tel" optional />
              </div>
              <Area ctx={ctx} prefix={P} k="message" label={t("form.message")} optional />
            </FormGroup>
            <FormFooter ctx={ctx} prefix={P} submitLabel={t("sellPage.submit")} />
          </form>
        )}
      </FormLayout>
    </div>
  );
}
